import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ClockPickerView } from "@mui/x-date-pickers";
import { useFormContext, useWatch } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { ExpandMore } from "@mui/icons-material";
import { useRecoilState, useRecoilValue } from "recoil";
import { useParams, useSearchParams } from "react-router-dom";
import { FormAutocomplete, FormDateTimePicker } from "../../../../components/fields";
import { accordionExpandedState, visibleCarToReplaceAccordion } from "../../recoil";
import { IFormValues } from "../../types";
import { RentalLocationType, RouteParams } from "../../../../types";
import { useBookingSubstitutionQueryObserver } from "../../../../api/services";
import { shouldDisableRentalDate, shouldDisableRentalTime } from "../../../../utils";

const CarToReplace = () => {
    const { t } = useTranslation('booking');
    const { id } = useParams<RouteParams>();
    const [queryParameters] = useSearchParams();
    const { setValue,getValues } = useFormContext<IFormValues>();
    const substitutionQuery = useBookingSubstitutionQueryObserver(id);
    const [carToReplaceExpanded, setCarToReplaceExpanded] = useRecoilState(accordionExpandedState('carToReplace'));
    const carToReplaceAccordion = useRecoilValue(visibleCarToReplaceAccordion);
    const dropoffDateRa = useWatch<IFormValues, "originRaData.dropoffDate">({
        name: 'originRaData.dropoffDate'
    });

    const eventInfoDate = useWatch<IFormValues, "eventInfo.eventDate">({
        name: 'eventInfo.eventDate'
    });

    const today = useMemo(() => dayjs().hour(0).minute(0), [t]);
    const shouldDisableDate = useCallback((date: dayjs.Dayjs) => {
        return shouldDisableRentalDate(date, getValues('carToReplace.dropoffLocation'), RentalLocationType.Dropoff, [], undefined);
    }, []);
    const shouldDisableTime = useCallback((time: number, clockTime: ClockPickerView) => {
        return shouldDisableRentalTime(time, clockTime, RentalLocationType.Dropoff, getValues('carToReplace.dropoffLocation'), getValues('carToReplace.dropoffDate'));
    }, []);

    const recoveryMode = queryParameters.get('recoveryMode') === '1' || false

    const maxDate = useMemo(() => {
        if (!dropoffDateRa) return undefined;

        const dropoff = dayjs(dropoffDateRa);
        const now = dayjs();

        if (recoveryMode) {
            return now.isAfter(dropoff) ? dropoff : now;
        }

        return dropoff;
    }, [dropoffDateRa, recoveryMode]);

    useEffect(() => {
        const now = dayjs();
        const dropOff = dayjs(dropoffDateRa);
        if(recoveryMode && dropoffDateRa && dropOff.isBefore(now)){
            setValue('carToReplace.dropoffDate',dropoffDateRa)
        }  
    }, [dropoffDateRa, recoveryMode])
    

    return (
        <>
            {carToReplaceAccordion &&
                <Grid item xs={12}>
                    <Accordion expanded={carToReplaceExpanded} onChange={(e, expanded) => setCarToReplaceExpanded(expanded)}>
                        <AccordionSummary expandIcon={<ExpandMore />}
                            sx={{
                                '.MuiAccordionSummary-content': {
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                }
                            }}
                        >
                            <Typography variant="h5">
                                {t('label.carToReplace_title')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid item container rowSpacing={1} columnSpacing={3}>
                                <Grid item xs={12} sm={3}>
                                    <FormAutocomplete<IFormValues>
                                        name="carToReplace.dropoffLocation"
                                        label={t('label.dropoffLocation')}
                                        options={substitutionQuery.data?.locations ?? []}
                                        loading={substitutionQuery.isLoading}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormDateTimePicker<IFormValues>
                                        name="carToReplace.dropoffDate"
                                        label={t('label.dropoffDateTime')}
                                        maxDateTime={maxDate}
                                        minDateTime={recoveryMode ? eventInfoDate! : today}
                                        shouldDisableDate={shouldDisableDate}
                                        shouldDisableTime={shouldDisableTime}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            }
        </>
    );
}

export default CarToReplace;
